import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Flame, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import TranslateButton from "./TranslateButton";

export default function Navbar() {
  const { t } = useLang();
  const { openBooking } = useBooking();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/astrology", label: t("nav_astrology") },
    { to: "/knowledge", label: t("nav_knowledge") },
    { to: "/life-concerns", label: t("nav_concerns") },
    { to: "/festivals", label: t("nav_festivals") },
    { to: "/visit", label: t("nav_visit") },
    { to: "/about", label: t("nav_about") },
    { to: "/contact", label: t("nav_contact") },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-tara-gold/20 shadow-[0_10px_40px_-24px_rgba(12,44,48,0.35)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <span className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-tara-gold to-tara-vermilion shadow-soft flame-flicker">
            <Flame className="h-5 w-5 text-white" />
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-lg font-bold gold-text tracking-wide">Tarapith</span>
            <span className="block font-serif text-[10px] tracking-[0.38em] text-tara-muted uppercase -mt-0.5">Tantrik</span>
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-5">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors relative group ${
                  active ? "text-tara-sindoor" : "text-tara-body hover:text-tara-sindoor"
                }`}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-[2px] rounded-full bg-gradient-to-r from-tara-vermilion to-tara-gold transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
        </div>

        <div className="hidden xl:flex items-center gap-2.5">
          <TranslateButton />
          <button
            onClick={() => openBooking("maa-tara-puja")}
            className="btn-sacred flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-serif font-semibold whitespace-nowrap"
            data-testid="navbar-book-btn"
          >
            <MessageCircle className="h-4 w-4" />
            {t("cta_book")}
          </button>
        </div>

        <button
          className="xl:hidden flex h-10 w-10 items-center justify-center rounded-xl border border-tara-gold/30 bg-white/70 text-tara-sindoor"
          onClick={() => setOpen((o) => !o)}
          data-testid="navbar-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-tara-gold/20"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-1">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="block text-tara-ink font-medium py-2.5 border-b border-tara-gold/10"
                  data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                >
                  {l.label}
                </Link>
              ))}
              <div className="pt-4"><TranslateButton /></div>
              <button
                onClick={() => openBooking("maa-tara-puja")}
                className="btn-sacred w-full mt-3 rounded-full px-5 py-3.5 text-sm font-serif font-semibold"
                data-testid="mobile-book-btn"
              >
                {t("cta_book")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
